import { useState, useEffect, useMemo } from "react";
import styled, { useTheme } from "styled-components";
import {
  MapPin,
  SunHorizon,
  Sun,
  CloudSun,
  CloudMoon,
  Moon,
} from "@phosphor-icons/react";
import { usePrayerMode } from "../../providers/PrayerModeProvider";
import {
  getPrayerTimes,
  type PrayerTimes as Times,
} from "../../utils/getPrayerTimes";

type ShiaKey = "Fajr" | "Shorooq" | "Dhuhr" | "Maghrib";
type SunniKey = "Fajr" | "Shorooq" | "Dhuhr" | "Asr" | "Maghrib" | "Isha";
type AnyKey = ShiaKey | SunniKey;
type Dir = "rtl" | "ltr";

const ORDER_SHIA: ShiaKey[] = ["Fajr", "Shorooq", "Dhuhr", "Maghrib"];
const ORDER_SUNNI: SunniKey[] = [
  "Fajr",
  "Shorooq",
  "Dhuhr",
  "Asr",
  "Maghrib",
  "Isha",
];

const LABELS_SHIA: Record<ShiaKey, { ar: string; en: string }> = {
  Fajr: { ar: "الفجر", en: "Fajr" },
  Shorooq: { ar: "الشروق", en: "Sunrise" },
  Dhuhr: { ar: "الظهرين", en: "Dhuhrayn" },
  Maghrib: { ar: "العشائين", en: "Maghribayn" },
};

const LABELS_SUNNI: Record<SunniKey, { ar: string; en: string }> = {
  Fajr: { ar: "الفجر", en: "Fajr" },
  Shorooq: { ar: "الشروق", en: "Sunrise" },
  Dhuhr: { ar: "الظهر", en: "Dhuhr" },
  Asr: { ar: "العصر", en: "Asr" },
  Maghrib: { ar: "المغرب", en: "Maghrib" },
  Isha: { ar: "العشاء", en: "Isha" },
};

const ICONS: Record<AnyKey, React.ElementType> = {
  Fajr: Moon,
  Shorooq: SunHorizon,
  Dhuhr: Sun,
  Asr: CloudSun,
  Maghrib: CloudMoon,
  Isha: Moon,
};

export default function PrayerTimes() {
  const { mode } = usePrayerMode();
  const [times, setTimes] = useState<Times | null>(null);
  const [nextPrayer, setNextPrayer] = useState<AnyKey | null>(null);
  const { dir } = useTheme() as { dir: Dir };

  useEffect(() => {
    (async () => {
      const t = await getPrayerTimes({ mode });
      setTimes(t);
    })();
  }, [mode]);

  const LABELS = useMemo<Record<AnyKey, string>>(() => {
    const base = mode === "shia" ? LABELS_SHIA : LABELS_SUNNI;
    return Object.fromEntries(
      Object.entries(base).map(([k, v]) => [k, dir === "rtl" ? v.ar : v.en])
    ) as Record<AnyKey, string>;
  }, [mode, dir]);

  useEffect(() => {
    if (!times) return;
    const ORDER = mode === "shia" ? ORDER_SHIA : ORDER_SUNNI;
    const toMin = (hhmm?: string) => {
      if (typeof hhmm !== "string") return NaN;
      const m = hhmm.match(/^(\d{1,2}):(\d{2})$/);
      if (!m) return NaN;
      return Number(m[1]) * 60 + Number(m[2]);
    };
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    const list = ORDER.map((key) => {
      const v = (times as any)?.[key] as string | undefined;
      return { key, minutes: toMin(v) };
    }).filter((p) => !Number.isNaN(p.minutes));

    const upcoming =
      list.find((p) => p.minutes > currentMinutes)?.key ?? ORDER[0];
    setNextPrayer(upcoming);
  }, [times, mode]);

  return (
    <Card $dir={dir}>
      <Header>
        <Location>
          <MapPin size={18} weight="bold" color="var(--primary)" />
          <span>{dir === "rtl" ? "المنامة، البحرين" : "Manama, Bahrain"}</span>
        </Location>
        <ModeNote>
          {mode === "shia"
            ? dir === "rtl"
              ? "حسب المذهب الشيعي"
              : "According to the Shia school"
            : dir === "rtl"
            ? "حسب المذهب السني"
            : "According to the Sunni school"}
        </ModeNote>
      </Header>

      <PrayerGrid>
        {(mode === "shia" ? ORDER_SHIA : ORDER_SUNNI).map((key) => {
          const Icon = ICONS[key];
          return (
            <PrayerCard key={key} $highlight={key === nextPrayer}>
              <Icon size={24} weight="duotone" color="var(--text)" />
              <span className="label">{LABELS[key]}</span>
              <span className="time">{(times as any)?.[key] ?? "--:--"}</span>
            </PrayerCard>
          );
        })}
      </PrayerGrid>
    </Card>
  );
}

/* --- styles --- */
const Card = styled.div<{ $dir: Dir }>`
  background: var(--surface);
  border-radius: 20px;
  padding: clamp(24px, 4vw, 32px);
  box-shadow: 0 4px 20px rgba(0,0,0,.08), 0 2px 6px rgba(0,0,0,.04);
  border: 1px solid var(--border);
  margin-bottom: 40px;
  backdrop-filter: blur(8px);
`;
const Header = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
  text-align: start;
  align-items: flex-start;
  padding-bottom: 16px;
  border-bottom: 1px solid color-mix(in oklab, var(--border) 60%, transparent);
`;
const Location = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 1rem;
  font-weight: 500;
  color: var(--text);
  span {
    order: 2;
  }
  svg {
    order: 1;
  }
`;
const ModeNote = styled.span`
  font-size: 0.8rem;
  color: var(--text-secondary);
`;
const PrayerGrid = styled.div`
  display: grid;
  gap: 14px;
  justify-content: space-between;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;
const PrayerCard = styled.div<{ $highlight?: boolean }>`
  border-radius: 14px;
  padding: 16px 10px;
  text-align: center;
  background: var(--background);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  border: 1.5px solid transparent;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  ${({ $highlight }) =>
    $highlight &&
    `border-color: var(--primary); 
     background: linear-gradient(135deg, color-mix(in oklab, var(--primary) 8%, var(--background)), var(--background));
     box-shadow: 0 4px 12px color-mix(in oklab, var(--primary) 25%, transparent), 0 2px 4px rgba(0,0,0,.08);
     transform: scale(1.05);`}
  &:hover {
    transform: ${({ $highlight }) => ($highlight ? 'scale(1.05)' : 'translateY(-2px)')};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  .label {
    display: block;
    font-size: 0.95rem;
    margin-top: 6px;
    color: var(--text);
    font-weight: 500;
  }
  .time {
    display: block;
    font-size: 1.15rem;
    font-weight: 600;
    margin-top: 4px;
    color: var(--text);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  }
`;
